import React from 'react';

import {
  Grid,
  Section,
  Button,
  EditNoteIcon,
  normalizeSelectValue,
  Alert,
} from '@gogame/components';
import { FormField, withForm, SidePageV2 } from '@gogame/form';
import { useScreenModalV2 } from '@gogame/screens';
import { useDateFormat } from '@gogame/intl-ts';

import ConceptFamiliesMultiSelect from 'scenes/Gallery/GalleryPage/Concepts/components/ConceptFamiliesMultiSelect';
import StatusSchedulesField from 'scenes/Gallery/GalleryPage/Concepts/components/StatusSchedulesField';
import * as CONCEPT from 'scenes/Gallery/GalleryPage/Concepts/constants';
import * as CREATIVE from 'scenes/Gallery/GalleryPage/Creatives/constants';
import {
  CREATIVE_TYPE_BANNER,
  CREATIVE_TYPE_VIDEO,
} from 'scenes/Gallery/GalleryPage/constants';
import {
  loadApplications,
  loadAllBusinessPartners,
  loadPromoCodes,
  loadRequestedBy,
  loadTags,
} from 'scenes/Gallery/GalleryPage/Concepts/sources/fields';
import { useGalleryPermissions } from 'scenes/Gallery/GalleryPage/hooks';
import {
  usePromoCodeDetails,
  useUploadAttachments,
} from 'scenes/Gallery/GalleryPage/Concepts/data';
import TagsMultiSelect from 'scenes/GalleryTags/components/TagsMultiSelect';

import BusinessPartnersFormField from './components/BusinessPartnersFormField';
import SimilarConcepts from './components/SimilarConcepts';

import ConceptPrefillFieldsDialog, {
  PREFILL_CONCEPT_AND_CREATIVE,
} from './ConceptPrefillFieldsDialog';
import Creative from './Creative';

const CreateConceptForm = withForm({
  initialNormalized: false,
  enableReinitialize: true,
})(({
  children,
  values,
  setValues,
  loading,
  onClose,
  onExited,
  title,
  fileInfo,
  submitButtonLabel = 'Save',
  open,
}) => {
  const {
    isAllowConceptFamilies,
    isAllowRequestedBy,
    isAllowJiraTask,
    isAllowBusinessPartner,
    isAllowSimilarConcepts,
    isAllowPromoCodes,
    isAllowChangeConceptStatus,
    isAllowAttachments,
    isAllowAddTags,
    isAllowDesigner,
    isAllowOutsourceStudio,
    isAllowExclusiveForAffiliates,
    isAllowTested,
    isAllowProductionType,
  } = useGalleryPermissions();

  const uploadAttachments = useUploadAttachments();
  const { data: promoCodeDetails } = usePromoCodeDetails(
    {
      id: values[CONCEPT.PROMO_CODE],
    },
    {
      enabled: !!values[CONCEPT.PROMO_CODE],
    }
  );
  const showModal = useScreenModalV2(ConceptPrefillFieldsDialog);
  const dateFormat = useDateFormat({
    template: 'LL',
  });
  const handlePrefillFields = async () => {
    const { conceptData, conceptOriginalData, prefillFieldFor } =
      await showModal();

    const {
      [CONCEPT.APPLICATIONS]: applications,
      [CONCEPT.BUSINESS_PARTNERS]: businessPartners,
      [CONCEPT.CONCEPT_FAMILIES]: conceptFamilies,
      [CONCEPT.PROMO_CODE]: promoCode,
      [CONCEPT.TAGS]: tags,
      [CONCEPT.JIRA_TASK]: jiraTask,
      [CONCEPT.REQUESTED_BY]: requestedBy,
      [CONCEPT.DESCRIPTION]: description,
    } = conceptData;

    const {
      [CREATIVE.COLOR]: color,
      [CREATIVE.NETWORKS]: networks,
      [CREATIVE.CHANNELS]: channels,
      [CREATIVE.EXCLUSIVE_FOR_AFFILIATES]: exclusiveForAffiliates,
      [CREATIVE.OS]: os,
      [CREATIVE.LANGUAGE]: language,
      [CREATIVE.ENHANCERS]: enhancers,
      [CREATIVE.TESTED]: tested,
      [CREATIVE.OUTSOURCE_STUDIO_ID]: outsourceStudio,
      [CREATIVE.DESIGNER]: designer,
      [CREATIVE.YOUTUBE_LINK]: youtubeLink,
      [CREATIVE.AI_GENERATED]: aiGenerated,
      [CREATIVE.PRODUCTION_TYPE]: productionType,
    } = conceptOriginalData;

    setValues({
      ...values,
      ...(isAllowBusinessPartner
        ? {
            [CONCEPT.BUSINESS_PARTNERS]: businessPartners,
          }
        : null),
      [CONCEPT.APPLICATIONS]: applications,
      ...(isAllowConceptFamilies
        ? {
            [CONCEPT.CONCEPT_FAMILIES]: conceptFamilies,
          }
        : null),
      ...(isAllowPromoCodes ? { [CONCEPT.PROMO_CODE]: promoCode } : null),
      [CONCEPT.TAGS]: tags,
      ...(isAllowJiraTask ? { [CONCEPT.JIRA_TASK]: jiraTask } : null),
      ...(isAllowRequestedBy ? { [CONCEPT.REQUESTED_BY]: requestedBy } : null),
      [CONCEPT.DESCRIPTION]: description,
      ...(prefillFieldFor === PREFILL_CONCEPT_AND_CREATIVE
        ? {
            [CONCEPT.ORIGINAL]: {
              ...values[CONCEPT.ORIGINAL],
              [CREATIVE.COLOR]: color,
              [CREATIVE.NETWORKS]: networks,
              [CREATIVE.CHANNELS]: channels,
              ...(isAllowExclusiveForAffiliates
                ? {
                    [CREATIVE.EXCLUSIVE_FOR_AFFILIATES]: exclusiveForAffiliates,
                  }
                : null),
              [CREATIVE.OS]: os,
              [CREATIVE.LANGUAGE]: language,
              [CREATIVE.ENHANCERS]: enhancers,
              ...(isAllowTested
                ? {
                    [CREATIVE.TESTED]: tested,
                  }
                : null),
              ...(isAllowOutsourceStudio
                ? {
                    [CREATIVE.OUTSOURCE_STUDIO_ID]: outsourceStudio,
                  }
                : null),
              ...(isAllowDesigner
                ? {
                    [CREATIVE.DESIGNER]: designer,
                  }
                : null),
              [CREATIVE.YOUTUBE_LINK]: youtubeLink,
              [CREATIVE.AI_GENERATED]: aiGenerated,
              ...(isAllowProductionType
                ? {
                    [CREATIVE.PRODUCTION_TYPE]: productionType,
                  }
                : null),
            },
          }
        : null),
    });
  };

  const creativeType = values[CONCEPT.ORIGINAL]?.[CREATIVE.CREATIVE_TYPE];

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={title} />
      <SidePageV2.NewBody dense={true}>
        <Section
          title={'Concept'}
          action={
            creativeType === CREATIVE_TYPE_BANNER ||
            creativeType === CREATIVE_TYPE_VIDEO ? (
              <Button
                variant={'outlined'}
                icon={<EditNoteIcon />}
                onClick={handlePrefillFields}
              >
                Prefill fields from...
              </Button>
            ) : null
          }
        >
          <Grid container spacing={3}>
            <Grid item xs={4}>
              <FormField.TextField
                label={'Name'}
                name={CONCEPT.NAME}
                helper={'1-3 words, 120 characters maximum'}
                debounceTimeout={500}
                required={true}
                padding={0}
              />
            </Grid>
            {isAllowBusinessPartner ? (
              <Grid item xs={4}>
                <BusinessPartnersFormField
                  label={'On behalf of BP'}
                  name={CONCEPT.BUSINESS_PARTNERS}
                  source={loadAllBusinessPartners}
                  padding={0}
                />
              </Grid>
            ) : null}
            <Grid item xs={4}>
              <FormField.MultiSelect
                label={'Applications'}
                name={CONCEPT.APPLICATIONS}
                source={loadApplications}
                required={true}
                padding={0}
              />
            </Grid>
            {isAllowSimilarConcepts ? (
              <SimilarConcepts name={values?.[CONCEPT.NAME]} />
            ) : null}
            {isAllowConceptFamilies ? (
              <Grid item xs={4}>
                <ConceptFamiliesMultiSelect />
              </Grid>
            ) : null}
            {isAllowPromoCodes ? (
              <Grid item xs={8}>
                <FormField.Select
                  label={'Promo code'}
                  name={CONCEPT.PROMO_CODE}
                  depends={[CONCEPT.APPLICATIONS]}
                  source={loadPromoCodes}
                  withDeselectButton={true}
                  normalizeValue={normalizeSelectValue}
                  padding={0}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <TagsMultiSelect
                source={loadTags}
                name={CONCEPT.TAGS}
                withAddTags={isAllowAddTags}
              />
            </Grid>
            {isAllowJiraTask ? (
              <Grid item xs={4}>
                <FormField.TextField
                  label={'Jira task URL'}
                  name={CONCEPT.JIRA_TASK}
                  padding={0}
                />
              </Grid>
            ) : null}
            {isAllowRequestedBy ? (
              <Grid item xs={4}>
                <FormField.Select
                  label={'Requested by'}
                  name={CONCEPT.REQUESTED_BY}
                  source={loadRequestedBy}
                  required={true}
                  padding={0}
                />
              </Grid>
            ) : null}
            <Grid item xs={12}>
              <FormField.TextField
                label={'Description'}
                name={CONCEPT.DESCRIPTION}
                multiline={true}
                padding={0}
              />
            </Grid>
          </Grid>
        </Section>
        {isAllowAttachments ? (
          <Section title={'Attachments'}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormField.AttachmentField
                  name={CONCEPT.ATTACHMENTS}
                  label={'Select files'}
                  tooltip={'Only pdf or jpg (jpeg) files are allowed'}
                  accept={'.pdf, .jpg, .jpeg'}
                  onUpload={uploadAttachments}
                  variant={'outlined'}
                  multiple={true}
                  padding={0}
                />
              </Grid>
            </Grid>
          </Section>
        ) : null}
        <Section title={'Original Creative'}>
          <Creative name={CONCEPT.ORIGINAL} fileInfo={fileInfo} />
        </Section>
        {isAllowChangeConceptStatus ? (
          <Section title={'Status'}>
            <Grid container spacing={3}>
              <Grid item xs={4}>
                <StatusSchedulesField
                  name={CONCEPT.STATE_SCHEDULES}
                  padding={0}
                />
              </Grid>
            </Grid>
          </Section>
        ) : null}
        {promoCodeDetails?.expirationDate ? (
          <Alert
            severity={'info'}
            title={`Promo Code expiration date - ${dateFormat(promoCodeDetails.expirationDate)}`}
          >
            Note: This creative will be archived based on the promo code's end
            date. If a different end date is set, the earliest date will take
            priority
          </Alert>
        ) : null}
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton>{submitButtonLabel}</SidePageV2.SubmitButton>
      </SidePageV2.Actions>
      {children}
    </SidePageV2>
  );
});

export default CreateConceptForm;
